import React from 'react';

export type StatusType = 'active' | 'disabled' | 'inactive' | 'pending' | 'blocked' | 'deleted';

interface StatusBadgeProps {
  status: StatusType | boolean | string;
  activeLabel?: string;
  inactiveLabel?: string;
  className?: string;
}

/**
 * Reusable Status Badge Component
 * Displays status with consistent styling across admin section
 * 
 * @param status - Status value (boolean, StatusType, or custom string)
 * @param activeLabel - Custom label for active/true state (default: 'Active')
 * @param inactiveLabel - Custom label for inactive/false state (default: 'Disabled')
 * @param className - Additional CSS classes
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  activeLabel = 'Active',
  inactiveLabel = 'Disabled',
  className = '',
}) => {
  // Normalize status to string
  const normalizedStatus = typeof status === 'boolean'
    ? (status ? 'active' : 'disabled')
    : String(status).toLowerCase();

  // Determine display text
  const getDisplayText = (): string => {
    if (typeof status === 'boolean') {
      return status ? activeLabel : inactiveLabel;
    }
    
    // Capitalize first letter for display
    return String(status).charAt(0).toUpperCase() + String(status).slice(1);
  };

  // Determine color classes based on status
  const getColorClass = (): string => {
    switch (normalizedStatus) {
      case 'active':
        return 'text-green-600';
      case 'disabled':
      case 'inactive':
        return 'text-red-600';
      case 'pending':
        return 'text-yellow-600';
      case 'blocked':
        return 'text-orange-600';
      case 'deleted':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <span className={`font-medium ${getColorClass()} ${className}`}>
      {getDisplayText()}
    </span>
  );
};

export default StatusBadge;

